/**
 * What the platform has actually texted.
 *
 * Until now every SMS went out and disappeared — the row was written to
 * `sms_log` and nobody could see it without a database client. This panel is
 * that log, plus the two things that come back from it: replies, and numbers
 * that have asked to stop.
 *
 * Three deliberate choices:
 *
 *  - "Sent" and "Delivered" are shown as separate facts. The provider accepting
 *    a message and a handset receiving it are different things, and treating
 *    them as one is how you tell a buyer their invoice went out when it never
 *    landed.
 *  - Message bodies are shown in full on click, not truncated away. If someone
 *    is checking what a customer was told, a summary is useless.
 *  - If migration 024 has not been run, the panel says so instead of showing an
 *    empty table, which would read as "nothing has happened".
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchSmsLog,
  fetchSmsReplies,
  markReplyHandled,
  summarise,
  SentFilter,
  SmsLogRow,
  SmsReplyRow,
} from '../../services/smsLogService'
import { listOptOuts, removeOptOut, SmsOptOut } from '../../services/smsOptOutService'
import './SmsActivityPanel.css'

type View = 'sent' | 'replies' | 'optouts'

const FILTERS: { id: SentFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'failed', label: 'Failed to send' },
  { id: 'undelivered', label: 'Not delivered' },
  { id: 'awaiting', label: 'No receipt yet' },
]

function when(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function money(n: number): string {
  return `$${n.toFixed(2)}`
}

/** Nicely spaced Australian mobile, falling back to whatever we were given. */
function phone(v?: string | null): string {
  const d = String(v || '').replace(/\D/g, '')
  if (d.length === 11 && d.startsWith('61')) return `0${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}`
  if (d.length === 10 && d.startsWith('0')) return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`
  return v || '—'
}

/**
 * The trigger names are how the code refers to itself. Nobody reading this
 * screen thinks in terms of AR_SMS_OVERDUE_3, so the known ones get plain
 * English and anything unrecognised is tidied rather than hidden — a trigger
 * we forgot to name here should still be readable.
 */
const REASONS: Record<string, string> = {
  BOS_SMS_SENT: 'Vehicle Purchase Agreement to sign',
  INSPECTION_SMS_SENT: 'Inspection booked',
  INSPECTION_BOOKED: 'Inspection booked',
  AR_INVOICE_LINK_SMS: 'Invoice link',
  AR_SMS_DUE_SOON: 'Payment due tomorrow',
  AR_SMS_OVERDUE_1: 'Overdue — 1st reminder',
  AR_SMS_OVERDUE_3: 'Overdue — 3 days',
  AR_SMS_OVERDUE_7: 'Overdue — 7 days',
  SELLER_REMINDER: 'Seller reminder',
  admin_test: 'Test message',
}

function reason(trigger?: string | null): string {
  if (!trigger) return '—'
  if (REASONS[trigger]) return REASONS[trigger]
  return trigger.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * One badge that answers "did this reach them?" — which is the only question
 * anyone opens this screen to ask.
 */
function Outcome({ row }: { row: SmsLogRow }) {
  if (row.status === 'failed') {
    return <span className="sms-pill bad" title={row.error_detail || undefined}>Failed</span>
  }
  if (row.delivery_status === 'delivered') return <span className="sms-pill good">Delivered</span>
  if (row.delivery_status === 'undelivered') {
    return <span className="sms-pill bad" title={row.delivery_detail || undefined}>Not delivered</span>
  }
  if (row.delivery_status) return <span className="sms-pill">{row.delivery_status}</span>
  return <span className="sms-pill wait" title="The provider accepted it. No delivery receipt has come back yet.">Sent</span>
}

function MigrationNotice({ what }: { what: string }) {
  return (
    <div className="sms-empty warn">
      <b>{what} are not being recorded yet.</b>
      <p>
        Migration <code>024_sms_delivery_and_optouts.sql</code> has not been run on this database.
        Run it in the Supabase SQL editor and this will fill in from the next message onwards.
      </p>
    </div>
  )
}

export default function SmsActivityPanel({ showToast }: { showToast: (m: string, e?: boolean) => void }) {
  const [view, setView] = useState<View>('sent')
  const [filter, setFilter] = useState<SentFilter>('all')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  const [sent, setSent] = useState<SmsLogRow[]>([])
  const [sentError, setSentError] = useState<string | null>(null)
  const [replies, setReplies] = useState<SmsReplyRow[]>([])
  const [repliesMissing, setRepliesMissing] = useState(false)
  const [optOuts, setOptOuts] = useState<SmsOptOut[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    if (view === 'sent') {
      const r = await fetchSmsLog(filter)
      setSent(r.rows)
      setSentError(r.error)
    } else if (view === 'replies') {
      const r = await fetchSmsReplies()
      setReplies(r.rows)
      setRepliesMissing(r.needsMigration)
    } else {
      setOptOuts(await listOptOuts())
    }
    setLoading(false)
  }, [view, filter])

  useEffect(() => {
    load()
  }, [load])

  const totals = useMemo(() => summarise(sent), [sent])

  const visible = useMemo(() => {
    const t = search.trim().toLowerCase()
    if (!t) return sent
    // Numbers are stored as +61… but people type 04… — compare on the part
    // that is the same either way, otherwise searching your own number
    // silently finds nothing.
    const local = (v: string) => v.replace(/\D/g, '').replace(/^(?:61|0)/, '')
    const typed = local(t)
    return sent.filter(
      (r) =>
        r.message.toLowerCase().includes(t) ||
        (r.trigger_name || '').toLowerCase().includes(t) ||
        (typed.length >= 3 && local(String(r.to_number || '')).includes(typed))
    )
  }, [sent, search])

  const unhandledReplies = replies.filter((r) => !r.handled_at).length

  /**
   * Before migration 024 the delivery columns are not on the row at all, which
   * is a different thing from being there and null. Null means "sent, no
   * receipt yet". Absent means "we do not record receipts". Without this check
   * every message would sit at Sent forever and the screen would quietly imply
   * the carrier never confirmed any of them.
   */
  const receiptsMissing = sent.length > 0 && !sent.some((r) => 'delivery_status' in r)

  return (
    <div className="sms-activity">
      <div className="sms-activity-head">
        <h3>Message history</h3>
        <button className="abtn small" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div className="sms-subtabs">
        <button className={view === 'sent' ? 'on' : ''} onClick={() => setView('sent')}>
          Sent
        </button>
        <button className={view === 'replies' ? 'on' : ''} onClick={() => setView('replies')}>
          Replies{unhandledReplies ? <span className="sms-count">{unhandledReplies}</span> : null}
        </button>
        <button className={view === 'optouts' ? 'on' : ''} onClick={() => setView('optouts')}>
          Opted out
        </button>
      </div>

      {view === 'sent' && (
        <>
          {receiptsMissing && (
            <div className="sms-empty warn" style={{ marginBottom: 14 }}>
              <b>Delivery receipts are not being recorded yet.</b>
              <p>
                Everything below shows as <i>Sent</i>, which only means the carrier accepted it —
                not that it arrived. Run migration <code>024_sms_delivery_and_optouts.sql</code> and
                point Tall Bob at <code>/api/sms/webhook</code>, and delivery will be tracked from
                the next message onwards.
              </p>
            </div>
          )}

          <div className="sms-totals">
            <div className="sms-total">
              <span>{totals.total}</span>
              <label>Messages</label>
            </div>
            {!receiptsMissing && (
              <>
                <div className="sms-total good">
                  <span>{totals.delivered}</span>
                  <label>Delivered</label>
                </div>
                <div className="sms-total wait">
                  <span>{totals.awaitingReceipt}</span>
                  <label>No receipt</label>
                </div>
              </>
            )}
            <div className="sms-total bad">
              <span>{totals.failed + totals.undelivered}</span>
              <label>Failed or not delivered</label>
            </div>
            {!receiptsMissing && (
              <div className="sms-total">
                <span>{money(totals.spend)}</span>
                <label>Carrier cost</label>
              </div>
            )}
          </div>

          <div className="sms-toolbar">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                className={`sms-chip ${filter === f.id ? 'on' : ''}`}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
            <input
              className="admin-search"
              placeholder="Search number, message or trigger…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {sentError && !loading && (
            <div className="sms-empty warn">
              <b>Could not read the SMS log.</b>
              <p>{sentError}</p>
            </div>
          )}

          {!loading && !sentError && visible.length === 0 && (
            <div className="sms-empty">
              {sent.length === 0
                ? 'Nothing has been sent from this environment yet.'
                : 'No messages match that search.'}
            </div>
          )}

          {visible.length > 0 && (
            <div className="admin-table-wrap">
              <table className="admin-table sms-table">
                <thead>
                  <tr>
                    <th style={{ width: 130 }}>When</th>
                    <th style={{ width: 130 }}>To</th>
                    <th style={{ width: 150 }}>Why</th>
                    <th>Message</th>
                    <th style={{ width: 120 }}>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr
                      key={r.id}
                      className="clickable"
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    >
                      <td className="sms-dim">{when(r.created_at)}</td>
                      <td>{phone(r.to_number)}</td>
                      <td className="sms-dim" title={r.trigger_name || undefined}>{reason(r.trigger_name)}</td>
                      <td className={expanded === r.id ? 'sms-body open' : 'sms-body'}>
                        {r.message}
                        {expanded === r.id && (
                          <div className="sms-meta">
                            <span>From: {r.sender || '—'}</span>
                            <span>Provider: {r.provider || '—'}</span>
                            <span>ID: {r.provider_sms_id || r.provider_message_id || '—'}</span>
                            {r.segments ? <span>{r.segments} segments</span> : null}
                            {r.price ? <span>{money(Number(r.price))}</span> : null}
                            {r.delivered_at ? <span>Delivered {when(r.delivered_at)}</span> : null}
                            {r.error_detail ? <span className="bad">{r.error_detail}</span> : null}
                            {r.delivery_detail ? <span className="bad">{r.delivery_detail}</span> : null}
                          </div>
                        )}
                      </td>
                      <td>
                        <Outcome row={r} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {view === 'replies' && (
        <>
          {repliesMissing ? (
            <MigrationNotice what="Replies" />
          ) : !loading && replies.length === 0 ? (
            <div className="sms-empty">
              No replies yet. Replies only arrive if Tall Bob is pointed at
              <code> /api/sms/webhook</code> and messages go out from a longcode people can reply to.
            </div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table sms-table">
                <thead>
                  <tr>
                    <th style={{ width: 130 }}>When</th>
                    <th style={{ width: 130 }}>From</th>
                    <th>Said</th>
                    <th style={{ width: 150 }}>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {replies.map((r) => (
                    <tr key={r.id} className={r.handled_at ? 'sms-handled' : ''}>
                      <td className="sms-dim">{when(r.received_at)}</td>
                      <td>{phone(r.from_number)}</td>
                      <td className="sms-body open">
                        {r.body}
                        {r.is_opt_out && <span className="sms-pill bad" style={{ marginLeft: 8 }}>Opt-out</span>}
                      </td>
                      <td>
                        {r.handled_at ? (
                          <span className="sms-dim">Handled {when(r.handled_at)}</span>
                        ) : (
                          <button
                            className="abtn small"
                            onClick={async () => {
                              const res = await markReplyHandled(r.id)
                              showToast(res.ok ? 'Marked as handled.' : res.error || 'Could not update.', !res.ok)
                              if (res.ok) load()
                            }}
                          >
                            Mark handled
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {view === 'optouts' && (
        <>
          <p className="admin-hint">
            These numbers are skipped before every send. A number lands here when someone replies
            STOP, or when the carrier rejects a message because they opted out elsewhere. Only
            remove one if the person has asked to hear from us again.
          </p>
          {!loading && optOuts.length === 0 ? (
            <div className="sms-empty">Nobody has opted out.</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table sms-table">
                <thead>
                  <tr>
                    <th style={{ width: 140 }}>Number</th>
                    <th style={{ width: 120 }}>How</th>
                    <th>What they said</th>
                    <th style={{ width: 130 }}>When</th>
                    <th style={{ width: 90 }}>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {optOuts.map((o) => (
                    <tr key={o.id}>
                      <td>{phone(o.number)}</td>
                      <td className="sms-dim">{o.source}</td>
                      <td className="sms-body open">{o.reason || o.note || '—'}</td>
                      <td className="sms-dim">{when(o.created_at)}</td>
                      <td>
                        <button
                          className="abtn small"
                          onClick={async () => {
                            const res = await removeOptOut(o.id)
                            showToast(res.ok ? 'Removed — this number can be texted again.' : res.error || 'Could not remove.', !res.ok)
                            if (res.ok) load()
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
